// Customizable Area Start
import React from "react";
import {
  Box,
  Typography,
  styled,
  IconButton,
  TextField,
  Popover,
  MenuItem,
  Button,
} from "@mui/material";
import SentimentSatisfiedAltRoundedIcon from "@mui/icons-material/SentimentSatisfiedAltRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import data from "@emoji-mart/data";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Picker from "@emoji-mart/react";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChatDetailsController, {
  configJSON,
  Props,
} from "./ChatDetailsController.web";
import ChatRender from "./ChatRender.web";
import CallModal from "./CallModal.web";
import CustomAvatar from "../../../components/src/CustomAvatar.web";
import ConditionalRenderer from "../../../components/src/ConditionalRenderer.web";
import CustomConfirmationDialog from "../../../components/src/CustomConfirmationDialog.web";
import { MoreVerticalIcon, PhoneIcon, VideoIcon } from "./assets";
import Loader from "../../../components/src/Loader.web";
import VideoPreview from "../../../components/src/VideoPreview.web";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";
// Customizable Area End

export default class ChatDetails extends ChatDetailsController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { selectedChatUID, navigation, toggleChatSelection } = this.props;
    const {
      message,
      chats,
      isTyping,
      attachments,
      recipientInfo,
      chatOptionsAnchorEl,
      emojiAnchorEl,
      isDeleteChatDialog,
      isBlockChatDialog,
      isUnBlockChatDialog,
      isCallDialog,
      isLoadingMessages,
      loggedInUserId,
      isSendingMessages,
      isDeletingChats,
      isInfoDialog,
      infoType,
      isBlockingUser,
      isUnblockingUser,
      isVideoCall
    } = this.state;
    const chatOptionsOpen = Boolean(chatOptionsAnchorEl);
    const emojiPickerOpen = Boolean(emojiAnchorEl);
    const chatOptionPopupId = chatOptionsOpen
      ? "chat-options-popup"
      : undefined;
    const emojiPickerPopupId = emojiPickerOpen
      ? "emoji-picker-popup"
      : undefined;
    const isCallDisabled = recipientInfo?.blockedByMe || recipientInfo?.hasBlockedMe
    const isMessagesFieldDisabled = isSendingMessages || recipientInfo?.blockedByMe

    return (
      <MainWrapper>
        <HeadingWrapper>
          <UserInfoHeadingWrapper hidden={!recipientInfo}>
            {recipientInfo && (
              <>
                <Box display="flex" alignItems="center">
                  <CustomBackButton
                    size="small"
                    data-test-id="close-chat"
                    onClick={() => toggleChatSelection(selectedChatUID)}
                  >
                    <ArrowBackIosRoundedIcon fontSize="small" />
                  </CustomBackButton>
                  <CustomAvatar
                    isOnline={recipientInfo.status === "online"}
                    height={36}
                    width={36}
                    isShowStatus
                    name={recipientInfo.name}
                    url={recipientInfo.avatar}
                  />
                </Box>
                <UserName data-test-id="user-name">
                  {recipientInfo.name}
                  <TypingIndicator data-test-id="typing-indicator" isTyping={isTyping} >
                    {configJSON.typingText}
                  </TypingIndicator>
                </UserName>
              </>
            )}
          </UserInfoHeadingWrapper>
          <ChatControlsWrapper>
            <ChatControlIcon
              disabled={!recipientInfo || isCallDisabled}
              data-test-id="audio-call"
              onClick={() => this.initiateCall()}
            >
              <img src={PhoneIcon} alt="Audio call" />
            </ChatControlIcon>
            <ChatControlIcon
              disabled={!recipientInfo || isCallDisabled}
              data-test-id="video-call"
              onClick={() => this.initiateCall(true)}
            >
              <img src={VideoIcon} alt="Video call" />
            </ChatControlIcon>
            <ChatControlIcon
              disabled={!recipientInfo}
              data-test-id="chat-menu"
              aria-describedby={chatOptionPopupId}
              onClick={this.handleChatOptionsPopupClick}
            >
              <img src={MoreVerticalIcon} alt="Options" />
            </ChatControlIcon>
            <Popover
              open={chatOptionsOpen}
              id={chatOptionPopupId}
              data-test-id="chat-menu-popup"
              anchorEl={chatOptionsAnchorEl}
              onClose={this.handleChatOptionsPopupClose}
              {...configJSON.OPTIONS_POSITION_CONFIGURATION}
              slotProps={{
                paper: {
                  sx: {
                    boxShadow: "none",
                  },
                },
              }}
            >
              <ChatOptionsWrapper>
                {this.getChatMenus().map((menu) => {
                  const { icon, title, isDisabled, handleClick } = menu;
                  return (
                    <ChatOptionWrapper
                      key={title}
                      disabled={isDisabled}
                      data-test-id={`${title} menu`}
                      onClick={handleClick}
                    >
                      {icon}
                      <Typography>{title}</Typography>
                    </ChatOptionWrapper>
                  );
                })}
              </ChatOptionsWrapper>
            </Popover>
          </ChatControlsWrapper>
        </HeadingWrapper>
        <ChatContentWrapper>
          <Loader
            wrapperHeight="100%"
            loading={chats.length === 0 && isLoadingMessages}
          >
            {recipientInfo ? (
              <ChatsFieldWrapper>
                <ChatsListingWrapper>
                  <ChatsListInnerWrapper
                    ref={this.messagesEndRef}
                    className="custom_scroll"
                  >
                    <Loader
                      wrapperHeight="40px"
                      size={20}
                      loading={chats.length > 0 && isLoadingMessages}
                    />
                    {Object.entries(this.getChatsByDate(chats)).map(
                      ([date, chats]) => (
                        <React.Fragment key={date}>
                          <DaySeparator>{date}</DaySeparator>
                          {chats.map((chat) => (
                            <ChatRender
                              key={chat.id}
                              navigation={navigation}
                              loggedInUserId={loggedInUserId}
                              chatDetails={chat}
                            />
                          ))}
                        </React.Fragment>
                      )
                    )}
                    <ConditionalRenderer isVisible={recipientInfo.blockedByMe}>
                      <BlockedUserWrapper>
                        <Box>
                          <Typography>
                            {configJSON.youBlockedThisUserText}
                          </Typography>
                          <Button
                            data-test-id="unblock-user"
                            onClick={this.handleOpenUnBlockChatDialog}
                          >
                            {configJSON.unblockText}
                          </Button>
                        </Box>
                      </BlockedUserWrapper>
                    </ConditionalRenderer>
                  </ChatsListInnerWrapper>
                </ChatsListingWrapper>
                {attachments.length > 0 && (
                  <AttachmentPreviewWrapper isSending={isSendingMessages}>
                    {attachments.map((file: File, index: number) => {
                      const { name } = file
                      const url = URL.createObjectURL(file);
                      const isImage = file.type.startsWith("image/");
                      const isVideo = file.type.startsWith("video/");

                      return (
                        <PreviewItem key={index}>
                          {isImage ? (
                            <img src={url} alt={name} />
                          ) : isVideo ? (
                            <VideoPreview url={url} />
                          ) : (
                            <FileFallback>
                              <InsertDriveFileIcon sx={{ fontSize: 26, color: "#652784", marginBottom: "4px" }} />
                              <FileTypeText>{name.split('.').pop()?.toUpperCase()}</FileTypeText>
                              <FileName title={name}>{name}</FileName>
                            </FileFallback>
                          )}
                          <RemoveButton data-test-id="remove-attachment" onClick={() => this.removeAttachment(index)}>
                            <CloseRoundedIcon fontSize="small" />
                          </RemoveButton>
                        </PreviewItem>
                      );
                    })}
                  </AttachmentPreviewWrapper>
                )}

                <ChatMessageControlWrapper isDisabled={!!isMessagesFieldDisabled}>
                  <IconWrapper
                    data-test-id="add-emoji"
                    disabled={isMessagesFieldDisabled}
                    onClick={this.handleEmojiPickerClick}
                  >
                    <SentimentSatisfiedAltRoundedIcon />
                  </IconWrapper>
                  <Popover
                    open={emojiPickerOpen}
                    id={emojiPickerPopupId}
                    anchorEl={emojiAnchorEl}
                    data-test-id="emoji-picker-popup"
                    onClose={this.handleEmojiPickerClose}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          boxShadow: "none",
                          border: "1px solid #CBD5E1",
                          borderRadius: "12px",
                          marginBottom: "20px",
                        },
                      },
                    }}
                  >
                    <Picker
                      set="native"
                      data={data}
                      data-test-id="emoji-picker"
                      onEmojiSelect={this.handleEmojiSelect}
                    />
                  </Popover>
                  <IconWrapper
                    data-test-id="add-attachment"
                    disabled={isMessagesFieldDisabled}
                    onClick={this.handleAddAttachment}
                  >
                    <AttachFileRoundedIcon />
                    <input
                      hidden
                      multiple
                      type="file"
                      data-test-id="upload-attachments"
                      ref={this.inputFieldRef}
                      accept={configJSON.AllowedMimeTypes.join(", ")}
                      onChange={this.handleAttachmentChange}
                    />
                  </IconWrapper>
                  <MessageField
                    autoFocus
                    value={message}
                    autoComplete="off"
                    inputRef={this.messageFieldRef}
                    disabled={isMessagesFieldDisabled}
                    data-test-id="message-field"
                    placeholder={configJSON.typeAMessageText}
                    onKeyDown={this.handleMessageKeyDown}
                    onChange={this.handleMessageValueChange}
                  />
                  <SendButton
                    data-test-id="send-button"
                    disabled={
                      !(message.trim() || attachments.length > 0) ||
                      isSendingMessages
                    }
                    onClick={this.handleSendMessage}
                  >
                    {isSendingMessages ? (
                      <Loader wrapperHeight="100%" size={23} loading />
                    ) : (
                      <SendRoundedIcon />
                    )}
                  </SendButton>
                </ChatMessageControlWrapper>

                {
                  isCallDialog && (
                    <CallModal
                      isOpen={isCallDialog}
                      isVideoCall={isVideoCall}
                      data-test-id="call-modal"
                      recipientInfo={recipientInfo}
                      handleOpenInfoDialog={this.handleOpenInfoDialog}
                      handleUpdateCallMsg={this.handleUpdateCallMsg}
                      handleCloseAudioCallDialog={this.handleCloseCallDialog}
                    />
                  )
                }

                <CustomConfirmationDialog
                  isOpen={isBlockChatDialog}
                  data-test-id="confirmation-block"
                  isActionLoading={isBlockingUser}
                  title={`${configJSON.blockText} ${recipientInfo.name}`}
                  actionBtnTitle={configJSON.blockText}
                  subTitle={`${configJSON.blockUserSubTitlePreText} ${recipientInfo.name}?`}
                  handleAction={this.handleBlockUser}
                  handleClose={this.handleCloseBlockChatDialog}
                />

                <CustomConfirmationDialog
                  isOpen={isUnBlockChatDialog}
                  data-test-id="confirmation-unblock"
                  isActionLoading={isUnblockingUser}
                  title={`${configJSON.unblockText} ${recipientInfo.name}`}
                  actionBtnTitle={configJSON.unblockText}
                  subTitle={`${configJSON.unblockUserSubTitlePreText} ${recipientInfo.name}?`}
                  handleAction={this.handleUnblockUser}
                  handleClose={this.handleCloseUnBlockChatDialog}
                />
              </ChatsFieldWrapper>
            ) : (
              <NoChatMessage data-test-id="no-chat-message">
                {configJSON.noChatsSelectedMessage}
              </NoChatMessage>
            )}
          </Loader>
        </ChatContentWrapper>
        <CustomConfirmationDialog
          isOpen={isDeleteChatDialog}
          data-test-id="confirmation-delete"
          isActionLoading={isDeletingChats}
          title={configJSON.deleteChatText}
          actionBtnTitle={configJSON.deleteText}
          subTitle={configJSON.deleteChatSubTitleText}
          handleAction={this.handleDeleteChats}
          handleClose={this.handleCloseDeleteChatDialog}
        />
        <CustomInfoDialog
          title=""
          infoType={infoType}
          isOpen={isInfoDialog}
          data-test-id="info-dialog"
          handleClose={this.handleCloseInfoDialog}
        />
      </MainWrapper>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const MainWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const HeadingWrapper = styled(Box)({
  padding: "6px 10px",
  height: "56px",
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid #CBD5E1",
});

const ChatContentWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  overflow: "hidden",
});

const UserInfoHeadingWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: "12px",
  paddingLeft: "5px",
  "@media (max-width: 650px)": {
    gap: "8px",
    paddingLeft: "0px",
    "& .MuiAvatar-root": {
      height: "32px",
      width: "32px",
    },
  },
});

const CustomBackButton = styled(IconButton)({
  display: "none",
  marginRight: "2px",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  "@media (max-width: 650px)": {
    display: "block",
  },
});

const UserName = styled(Typography)({
  color: "#0F172A",
  fontSize: "0.875rem",
  fontWeight: 500,
  lineHeight: "normal",
  "@media (max-width: 650px)": {
    fontSize: "0.75rem",
  },
});

const TypingIndicator = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isTyping",
})<{ isTyping: boolean }>(({ isTyping }) => ({
  color: "#475569",
  fontSize: "11px",
  fontWeight: 400,
  lineHeight: "normal",
  maxHeight: isTyping ? "20px" : "0px",
  opacity: isTyping ? 1 : 0,
  overflow: "hidden",
  transition: "opacity 0.3s ease, max-height 0.3s ease"
}));

const ChatControlsWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  "& svg": {
    color: "#0F172A",
  },
});

const ChatControlIcon = styled(IconButton)({
  padding: "6px",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.08)",
  },
  "&.Mui-disabled img": {
    filter:
      "brightness(0) saturate(100%) invert(94%) sepia(60%) saturate(6013%) hue-rotate(175deg) brightness(80%) contrast(73%)",
  },
  "@media (max-width: 650px)": {
    "& img": {
      height: "20px",
      width: "20px",
    },
  },
});

const ChatOptionsWrapper = styled(Box)({
  border: "1px solid #CBD5E1",
  borderRadius: "8px",
  padding: "0px",
  minHeight: "116px",
  width: "140px",
  display: "flex",
  overflow: "hidden",
  flexDirection: "column",
});

const ChatOptionWrapper = styled(MenuItem)({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  flex: 1,
  minHeight: "30px",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  "& svg": {
    height: "20px",
    width: "20px",
  },
  "& p": {
    fontSize: "0.75rem",
    color: "#1E293B",
    fontWeight: 500,
    letterSpacing: "-0.15px",
  },
});

const NoChatMessage = styled(Typography)({
  color: "#94A3B8",
  fontSize: "2rem",
  fontWeight: 500,
  "@media (max-width: 850px)": {
    fontSize: "1.5rem",
  },
});

const ChatsFieldWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
});

const ChatsListingWrapper = styled(Box)({
  flex: 1,
  overflow: "hidden",
  margin: "15px 5px 5px",
  boxSizing: "border-box",
  "@supports (-webkit-touch-callout: none)": {
    height: "-webkit-fill-available",
    paddingBottom: "env(safe-area-inset-bottom)"
  }
});

const ChatsListInnerWrapper = styled(Box)({
  height: "100%",
  width: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  padding: "10px 2rem",
  WebkitOverflowScrolling: "touch",
  "@media (max-width: 650px)": {
    padding: "10px 0.5rem"
  },
  "@supports (-webkit-touch-callout: none)": {
    paddingBottom: "10px"
  }
});

const DaySeparator = styled(Typography)({
  textAlign: "center",
  color: "#64748B",
  fontSize: "0.75rem",
  fontWeight: 500,
  margin: "10px 0px",
});

const BlockedUserWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  "& > div": {
    boxShadow: "0px 4px 15px -3px rgba(101, 39, 132, 0.15)",
    height: "36px",
    padding: "0px 8px",
    width: "100%",
    maxWidth: "255px",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "3px",
    "& > p": {
      color: "#0F172A",
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: "24px",
    },
    "& > button": {
      color: "#652784",
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
      padding: "0",
      lineHeight: "24px",
      "&:hover": {
        fontWeight: 700,
        backgroundColor: "transparent"
      },
    },
  },
});

const ChatMessageControlWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDisabled",
})<{ isDisabled: boolean }>(({ isDisabled }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: "1px",
  border: "1px solid #CBD5E1",
  borderRadius: "30px",
  padding: "0 5px",
  height: "48px",
  margin: "0 2rem 1rem",
  backgroundColor: isDisabled ? "#F1F5F9" : "#fff",
  color: isDisabled ? "#94A3B8" : "inherit",
  pointerEvents: isDisabled ? "none" : "auto",
  opacity: isDisabled ? 0.6 : 1,
  filter: isDisabled ? "grayscale(1)" : "none",
  "@supports (-webkit-touch-callout: none)": {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    marginBottom: "env(safe-area-inset-bottom)"
  },
  "&::before": isDisabled
    ? {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        borderRadius: "30px",
        zIndex: 1,
      }
    : {},
  "@media (max-width: 650px)": {
    margin: "0 1rem 1rem",
  },
}));

const IconWrapper = styled(IconButton)({
  padding: "6px",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.08)",
  },
  "& svg": {
    color: "#94A3B8",
  },
});

const SendButton = styled(IconButton)({
  backgroundColor: "#652784",
  padding: "6px",
  "& svg": {
    color: "#fff",
  },
  "&:hover": {
    backgroundColor: "#652784",
  },
  "&.Mui-disabled": {
    backgroundColor: "#652784",
    opacity: 0.3,
  },
});

const MessageField = styled(TextField)({
  height: "100%",
  flex: 1,
  "& .MuiInputBase-root": {
    height: "100%",
  },
  "& input": {
    color: "#94A3B8",
    fontWeight: 500,
    fontSize: "1rem",
    padding: "4px",
    height: "100%",
    letterSpacing: "-0.15px",
  },
  "& fieldset": {
    border: "none",
  },
});

const AttachmentPreviewWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSending",
})<{ isSending?: boolean }>(({ isSending }) => ({
  display: "flex",
  overflowX: "auto",
  padding: "8px 12px",
  gap: "10px",
  background: "#f8fafc",
  borderTop: "1px solid #e2e8f0",
  borderBottom: "1px solid #e2e8f0",
  maxWidth: "100%",
  boxSizing: "border-box",
  maxHeight: "200px",
  margin: "0px 2rem 10px",
  pointerEvents: isSending ? "none" : "auto",
  opacity: isSending ? 0.6 : 1,
}));

const PreviewItem = styled(Box)({
  position: "relative",
  width: "100px",
  height: "100px",
  minWidth: "100px",
  minHeight: "100px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "8px",
  backgroundColor: "#e2e8f0",
  overflow: "hidden",
  padding: "4px",
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "6px",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "6px",
  },
});

const FileFallback = styled(Box)({
  textAlign: "center",
  width: "100%",
  padding: "4px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

const FileTypeText = styled(Box)({
  fontWeight: 600,
  fontSize: "13px",
  color: "#0F172A",
});

const FileName = styled("span")({
  fontSize: "12px",
  padding: "0 4px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  width: "100%",
  color: "#64748B",
  textAlign: "center",
});

const RemoveButton = styled(IconButton)({
  position: "absolute",
  top: 2,
  right: 4,
  backgroundColor: "#00000060",
  color: "#fff",
  borderRadius: "50%",
  width: "18px",
  height: "18px",
  fontSize: "12px",
  padding: "3px",
  "&:hover": {
    backgroundColor: "#00000080",
  }
});

// Customizable Area End
